import * as XLSX from 'xlsx/xlsx.mjs';
import type { AvailableSeries } from '@/types/series';
import type { SeriesDefinition } from '@/types/statisticsSeriesDefs';

type ExportPeriod = { start: string; end: string; delta?: { start: string; end: string } };
type ExportRow = { title: { name: string; id: string | number }[] } & Record<string, unknown>;
type ExportTable = { periods: ExportPeriod[]; rows: ExportRow[] };

type SheetRow = (string | number)[];
type WorkSheetData = SheetRow[];

export const exportTableToExcel = <Series extends AvailableSeries>(
  data: ExportTable,
  series: SeriesDefinition<Series>[],
  title: string,
  label: string,
) => {
  const { periods, rows } = data;

  const worksheetData: WorkSheetData = [];
  worksheetData.push([`Filter: ${label}`]);

  const firstRow = rows[0];
  const firstPeriod = periods.at(0);
  const lastPeriod = periods.at(-1);
  const hasDelta = (s: SeriesDefinition<Series>) => `${s.slug}:delta` in firstRow;

  const nameRow: SheetRow = ['Namn'];
  series.forEach((s) => {
    nameRow.push(s.title);
    if (hasDelta(s)) nameRow.push(s.delta === 'relative' ? '%' : 'Δ');
  });
  worksheetData.push(nameRow);

  const periodRow: SheetRow = ['Period'];
  series.forEach((s) => {
    periodRow.push(firstPeriod ? `${firstPeriod.start}–${lastPeriod?.end}` : '');
    if (hasDelta(s)) {
      const isDeltaPeriod = firstPeriod?.delta && lastPeriod?.delta;
      periodRow.push(isDeltaPeriod ? `${firstPeriod?.delta?.start}–${lastPeriod?.delta?.end}` : '');
    }
  });
  worksheetData.push(periodRow);

  rows.forEach((row) => {
    const [primary] = row.title;
    const sheetRow: SheetRow = [primary.name ?? ''];
    series.forEach((s) => {
      sheetRow.push((row[s.slug] as number | null) ?? '');
      if (hasDelta(s)) sheetRow.push((row[`${s.slug}:delta`] as number | null) ?? '');
    });
    worksheetData.push(sheetRow);
  });

  const workbook = XLSX.utils.book_new();
  [{ name: title, data: worksheetData }].forEach((s: { name: string; data: WorkSheetData }) => {
    const worksheet = XLSX.utils.aoa_to_sheet(s.data);
    XLSX.utils.book_append_sheet(workbook, worksheet, s.name);
  });

  XLSX.writeFile(workbook, `${title} för ${label}.xlsx`);

  return {
    message: 'Done',
    success: true,
  };
};
